import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Divider, Modal, Space, Table, Tag, Typography } from 'antd';
import {
    DollarOutlined,
    HistoryOutlined,
    RollbackOutlined,
    SwapOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import { checkPermission } from '@/utils/permission';
import { getCustomerDepositSummary, getCustomerFinanceSummary } from '@/redux/actions/receiptActions';
import { renderMoney } from '@/utils/helpers';
import CustomerRefundModal from './CustomerRefundModal';
import CustomerDepositActions from './CustomerDepositActions';
import CustomerFundTransferModal from './CustomerFundTransferModal';
import CustomerBalanceApplicationModal from './CustomerBalanceApplicationModal';
import { transferHistoryText } from './customerFundTransferPresentation';

const entryTypeLabels = {
    RECEIPT_OVERPAYMENT: 'Tiền thu chưa phân bổ',
    APPLIED_TO_CUSTOMER_SERVICE: 'Đã dùng số dư cho dịch vụ',
    REFUNDED_TO_CUSTOMER: 'Đã hoàn tiền cho khách',
    RECEIPT_BALANCE_REVERSAL: 'Điều chỉnh số dư do sửa phiếu thu',
    SERVICE_ADJUSTMENT_RELEASE: 'Số dư phát sinh do điều chỉnh dịch vụ',
    TRANSFERRED_OUT: 'Chuyển cho khách hàng khác',
    TRANSFERRED_IN: 'Nhận chuyển từ khách hàng khác',
};
const displayText = (value, fallback = '—') => value == null || value === '' ? fallback : String(value);
const { Text } = Typography;

const actionGroupStyle = {
    height: '100%',
    padding: 12,
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#fafafa',
};
const actionButtonStyle = {
    width: '100%',
    minHeight: 32,
    height: 'auto',
    padding: '5px 12px',
    textAlign: 'left',
    whiteSpace: 'normal',
    lineHeight: 1.4,
};
const depositButtonStyle = {
    ...actionButtonStyle,
    color: '#096dd9',
    borderColor: '#69c0ff',
    background: '#e6f7ff',
};
const transferButtonStyle = {
    ...actionButtonStyle,
    color: '#531dab',
    borderColor: '#b37feb',
    background: '#f9f0ff',
};
const actionGridStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
};
const actionGridItemStyle = {
    flex: '1 1 240px',
    minWidth: 0,
};

/** Các thao tác tài chính tổng hợp tại hồ sơ khách hàng. */
export default function CustomerFinanceActions({ customerId, onChanged }) {
    const dispatch = useDispatch();
    const [data, setData] = useState({ balance_history: [], refundable_amount: 0 });
    const [showHistory, setShowHistory] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    const [showTransfer, setShowTransfer] = useState(false);
    const [showBalanceApplication, setShowBalanceApplication] = useState(false);
    const canView = checkPermission('customer_refund', 'view');
    const canRefund = canView && checkPermission('customer_refund', 'execute');
    const canViewDeposit = checkPermission('customer_deposit', 'view');
    const canTransfer = checkPermission('customer_fund_transfer', 'execute');
    const canApplyBalance = canView && checkPermission('customer_balance', 'apply');

    const load = useCallback(() => {
        if (!customerId || (!canView && !canViewDeposit)) return Promise.resolve();
        return dispatch(canView ? getCustomerFinanceSummary(customerId) : getCustomerDepositSummary(customerId)).then(setData);
    }, [dispatch, customerId, canView, canViewDeposit]);

    useEffect(() => { load().catch(() => null); }, [load]);
    if (!canView && !canRefund && !canViewDeposit && !canTransfer && !canApplyBalance) return null;

    return <>
        <CustomerDepositActions customerId={customerId} data={data} reload={async () => {
            await load();
            if (onChanged) await onChanged();
        }} renderActions={({ openReceive, openDepositList, openDepositHistory, canReceive, canView: canViewDepositActions }) => <div>
            <div style={actionGridStyle}>
                {canReceive && <div style={actionGridItemStyle}>
                    <div style={actionGroupStyle}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong><DollarOutlined /> Tiền vào</Text>
                            <Text type="secondary">Ghi nhận khoản khách hàng đặt cọc.</Text>
                            <Button type="primary" icon={<DollarOutlined />} style={actionButtonStyle}
                                onClick={openReceive}>Nhận tiền cọc</Button>
                        </Space>
                    </div>
                </div>}

                {(canViewDepositActions || canTransfer) && <div style={actionGridItemStyle}>
                    <div style={actionGroupStyle}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong><SwapOutlined /> Sử dụng / chuyển tiền</Text>
                            <Text type="secondary">Dùng tiền cọc cho dịch vụ hoặc chuyển nguồn tiền.</Text>
                            {canViewDepositActions && <Button icon={<WalletOutlined />} style={depositButtonStyle}
                                onClick={openDepositList}>Quản lý tiền cọc</Button>}
                            {canApplyBalance && <Button icon={<WalletOutlined />} style={depositButtonStyle}
                                disabled={Number(data.unallocated_balance || 0) <= 0}
                                onClick={() => setShowBalanceApplication(true)}>Sử dụng số dư</Button>}
                            {canTransfer && <Button icon={<SwapOutlined />} style={transferButtonStyle}
                                onClick={() => setShowTransfer(true)}>Chuyển tiền cho khách khác</Button>}
                        </Space>
                    </div>
                </div>}

                {canRefund && <div style={actionGridItemStyle}>
                    <div style={actionGroupStyle}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Text strong><RollbackOutlined /> Tiền ra / hoàn đảo</Text>
                            <Text type="secondary">Hoàn lại nguồn tiền hợp lệ cho khách hàng.</Text>
                            <Button danger icon={<RollbackOutlined />} style={actionButtonStyle}
                                disabled={canView && Number(data.refundable_amount) <= 0}
                                onClick={() => setShowRefund(true)}>Hoàn tiền cho khách</Button>
                        </Space>
                    </div>
                </div>}
            </div>

            {/* {canView && data.commission_source_projection && <>
                <Divider style={{ margin: '12px 0 6px' }} />
                <Space size={16} wrap>
                    <Text>Đã phân bổ: <Text strong>{renderMoney(data.commission_source_projection.allocated_amount)}</Text></Text>
                    <Text>Đã ghi nhận hoa hồng: <Text strong>{renderMoney(data.commission_source_projection.recognized_amount)}</Text></Text>
                    <Text>Còn chờ ghi nhận: <Text strong>{renderMoney(data.commission_source_projection.pending_amount)}</Text></Text>
                </Space>
                {data.commission_source_projection.reconciliation_status === 'MISMATCH'
                    && <Alert style={{ marginTop: 8 }} type="error" message="Đối chiếu nguồn hoa hồng có chênh lệch; vui lòng kiểm tra trước khi chốt kỳ." />}
            </>} */}

            {(canView || canViewDepositActions) && <>
                <Divider style={{ margin: '12px 0 6px' }} />
                <Space size={4} wrap>
                    <Text type="secondary">Tra cứu:</Text>
                    {canView && <Button type="link" style={{ color: "#0071d8" }} size="small" icon={<HistoryOutlined />}
                        onClick={() => setShowHistory(true)}>Lịch sử số dư</Button>}
                    {canViewDepositActions && <Button type="link" style={{ color: "#0071d8" }} size="small" icon={<HistoryOutlined />}
                        onClick={openDepositHistory}>Lịch sử tiền cọc</Button>}
                </Space>
            </>}
        </div>} />
        <Modal visible={showHistory} destroyOnClose width={isMobile ? '100%' : '90%'} title="Lịch sử số dư khách hàng"
            footer={<Button type="primary" onClick={() => setShowHistory(false)}>Đóng</Button>}
            onCancel={() => setShowHistory(false)} maskClosable={false}>
            <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 10 }}
                dataSource={data.balance_history || []}
                scroll={{ x: 'max-content' }}
                columns={[
                    {
                        title: 'Thời gian',
                        dataIndex: 'effective_at',
                        width: 150,
                        render: value =>
                            value ? moment(value).format('DD/MM/YYYY HH:mm') : '—',
                    },
                    {
                        title: 'Nguồn phát sinh',
                        dataIndex: 'entry_type',
                        width: 230,
                        render: value => (
                            <Tag>{entryTypeLabels[value] || 'Điều chỉnh số dư'}</Tag>
                        ),
                    },
                    {
                        title: 'Giao dịch đối ứng',
                        width: 260,
                        render: (_, row) => transferHistoryText(row) || '—',
                    },
                    {
                        title: 'Loại nguồn',
                        width: 140,
                        render: (_, row) => row.transfer_code ? 'Số dư' : '—',
                    },
                    {
                        title: 'Phiếu thu',
                        dataIndex: 'receipt_code',
                        width: 105,
                        align: 'center',
                        render: (value, row) => row.transfer_code ? '—' : displayText(value),
                    },
                    {
                        title: 'Dịch vụ liên quan',
                        dataIndex: 'service_name',
                        width: 230,
                        render: value => displayText(value),
                    },
                    {
                        title: 'Số tiền',
                        dataIndex: 'amount',
                        width: 145,
                        align: 'right',
                        render: (value, row) =>
                            `${row.direction === 'DEBIT' ? '-' : '+'}${renderMoney(value)}`,
                    },
                    {
                        title: 'Người thực hiện',
                        dataIndex: 'actor_name',
                        width: 250,
                        render: value => displayText(value, 'Hệ thống'),
                    },
                    {
                        title: 'Nội dung',
                        dataIndex: 'reason',
                        width: 300,
                        render: value => displayText(value),
                    },
                ]}
            />
        </Modal>
        <CustomerRefundModal visible={showRefund} customerId={customerId} onCancel={() => setShowRefund(false)}
            onCompleted={async () => { await load(); if (onChanged) await onChanged(); }} />
        <CustomerFundTransferModal visible={showTransfer} customerId={customerId} onCancel={() => setShowTransfer(false)}
            onCompleted={async () => { await load(); if (onChanged) await onChanged(); }} />
        <CustomerBalanceApplicationModal visible={showBalanceApplication} customerId={customerId} finance={data}
            onCancel={() => setShowBalanceApplication(false)}
            onCompleted={async () => { await load(); if (onChanged) await onChanged(); }} />
    </>;
}
